import { Dialog } from "@headlessui/react"
import React, { FC, useEffect, useState } from "react"
import { X } from "heroicons-react"
import { DealOffer, User, UserExpense, UserExpenseForm } from "../../../../../types"
import { KButton, KInput, KSpan, KTextArea, KTitleSpan } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { errorFilledToastOptions, NOTIFICATION_TYPES, successFilledToastOptions } from "../../../../../constants"
import { toast } from "react-toastify"
import AdminPaymentMethodModal from "./AdminPaymentMethodModal"
import AdminUserExpenseModal from "./AdminUserExpenseModal"
import AdminUserContractModal from "./AdminUserContractModal"
import AdminUserAdvanceModal from "./AdminUserAdvanceModal"
import { formatNumber, getCurrency, mapToCamelCase } from "../../../../../utility"
import AdminCurrencySelectionModal from "./AdminCurrencySelectionModal"

type AdminAgreementsPayoutModalProps = {
  open: boolean
  onClose: () => void
  user: User
  loggedInUser: User
  paymentMethodsAdminEntry?: (userId?: number) => void
  onUserExpenseFormSubmit: (userExpenseForm: UserExpenseForm) => Promise<void>
  selectedUserExpense: UserExpense
}
const AdminAgreementsPayoutModal: FC<AdminAgreementsPayoutModalProps> = (props) => {
  const { onClose, open } = props

  const [confirmedOfferId, setConfirmedOfferId] = useState("")
  const [loading, setLoading] = useState(false)
  const [dealOffer, setDealOffer] = useState<DealOffer>()
  const [openCurrencySelection, setOpenCurrencySelection] = useState<boolean>(false)
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (props.open && props.user && props.user.userTakedown && props.user.userTakedown.confirmedOfferId) {
      setConfirmedOfferId(props.user.userTakedown.confirmedOfferId.toString())
      users(`/get_deal_offer_detail/?confirmed_offer_id=${props.user.userTakedown.confirmedOfferId}`, "get")
        .then((resp) => {
          if (resp && resp.status === 200 && resp.data) {
            setDealOffer(mapToCamelCase(resp.data))
          }
        })
        .catch((err) => {
          toast.error("Error fetching deal offer details.", errorFilledToastOptions)
        })
    }
  }, [props.user, props.open])

  const onCloseModal = () => {
    setConfirmedOfferId("")
    setDealOffer(undefined)
    onClose()
  }

  const onUpdateUserTakedown = () => {
    if (!props.user.id) return

    setLoading(true)
    users("/update_user_takedown/", "put", { user_id: props.user.id, confirmed_offer_id: confirmedOfferId }).then(
      (resp) => {
        setLoading(false)
        if (resp && resp.status === 200) {
          toast.success("User Takedown updated successfully.", successFilledToastOptions)

          onCloseModal()
        } else {
          toast.error("Error saving User Takedown.", errorFilledToastOptions)
        }
      }
    )
  }

  const renderUserTakedown = () => {
    return (
      <div className="mt-4">
        <KSpan text="User Takedown" fontWeight={500} />
        <div className="flex gap-2 bg-white elev-b-sm rounded-lg overflow-hidden p-6">
          <div className="flex gap-2 items-center">
            <KSpan text="Confirmed Offer ID (Scouting)" />
            <KInput
              placeholder="Enter to see deal"
              value={confirmedOfferId}
              onChange={(value) => setConfirmedOfferId(value)}
            />
          </div>
          <div className="flex">
            <KButton
              height="36px"
              dataTestId="user-takedown-confirm-button"
              text="Save User Takedown"
              background="black"
              textColor="white"
              onClick={() => onUpdateUserTakedown()}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderUserExpense = () => {
    return (
      <div>
        <KSpan text="User Expense" fontWeight={500} />
        <AdminUserExpenseModal
          open={true}
          onClose={onClose}
          selectedUserExpense={props.selectedUserExpense}
          onUserExpenseFormSubmit={props.onUserExpenseFormSubmit}
          user={props.user}
          loggedInUser={props.loggedInUser}
          isModal={false}
        />
      </div>
    )
  }

  const renderPaymentMethods = () => {
    return (
      <div className="mt-4">
        <KSpan text="Payment Methods" fontWeight={500} />
        <AdminPaymentMethodModal
          open={true}
          onClose={onClose}
          paymentMethodsAdminEntry={props.paymentMethodsAdminEntry}
          userId={props.user?.id}
          readOnly={false}
          isModal={false}
        />
      </div>
    )
  }

  const renderUserAdvance = () => {
    return (
      <div>
        <KSpan text="Advances" fontWeight={500} />
        <AdminUserAdvanceModal
          open={true}
          onClose={onClose}
          userId={props.user?.id}
          user={props?.user}
          setSelectedCurrencyId={setSelectedCurrencyId}
          onChangeCurrencyClick={() => {
            setOpenCurrencySelection(true)
          }}
          isModal={false}
        />
      </div>
    )
  }

  const renderUserContract = () => {
    return (
      <div className="mt-4">
        <KSpan text="User Contract" fontWeight={500} />
        <AdminUserContractModal open={true} onClose={onClose} userId={props.user?.id} isModal={false} />
      </div>
    )
  }

  const calculateContractEnds = (dealOffer: DealOffer) => {
    const signedAt = dealOffer.signedAt ? new Date(dealOffer.signedAt) : null
    if (!signedAt || !dealOffer.contractDuration) {
      return "-"
    }

    const contractLengthYears = parseInt(dealOffer.contractDuration, 10)

    return new Date(signedAt.getFullYear() + contractLengthYears, signedAt.getMonth(), signedAt.getDate())
      .toISOString()
      .substring(0, 10)
  }

  const Info = ({ label, value }: { label: string; value: string }) => (
    <div className="flex flex-col gap-1">
      <KSpan text={label} color="#111" fontWeight={500} />
      <KSpan text={value ?? "-"} />
    </div>
  )

  const renderDealDetails = () => {
    return (
      <div className="mt-4">
        <KSpan text="Deal Details" fontWeight={500} />
        {!dealOffer ? (
          <div className="bg-white elev-b-sm rounded-lg overflow-hidden p-6">
            <KSpan text="No Deal Offer. Update confirmed offer ID and refresh page" />
          </div>
        ) : (
          <div className="bg-white elev-b-sm rounded-lg overflow-hidden p-6">
            <div className="pb-3 pt-3">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 w-full">
                <Info label="Status" value={`${dealOffer.status || "-"} (v${dealOffer.version})`} />
                <Info
                  label="Offer"
                  value={`${formatNumber(dealOffer.offer?.toFixed(0) || "")} ${getCurrency(dealOffer.currency)}`}
                />

                <Info label="Signed At" value={dealOffer.signedAt?.substring(0, 10) || "-"} />
                <Info label="Contract Ends" value={calculateContractEnds(dealOffer)} />
                <Info label="Contract Length" value={dealOffer.contractDuration || "-"} />

                <Info label="Type" value={dealOffer.type || "-"} />

                <Info label="Included in Recoupment:" value={`${dealOffer.newReleaseIncludeRecoup}`} />

                <Info
                  label="New Release Pre / Post Commission %"
                  value={`${dealOffer.newReleaseCommissionRate || "-"} / ${
                    dealOffer.newReleasePostCommissionRate || "-"
                  }`}
                />
                <Info
                  label="Advance Pre / Post Commission %"
                  value={`${dealOffer.contractPreRecoupCommission || "-"} / ${
                    dealOffer.contractPostRecoupCommission || "-"
                  }`}
                />

                <Info label="Exclusivity" value={`${dealOffer.contractExclusivity}`} />

                <Info label="Recoup Base" value={dealOffer.recoupBase || "-"} />
                <Info label="Catalog Definition" value={dealOffer.catalogDefinition || "-"} />

                <Info label="Sync Rights" value={`${dealOffer.syncRights}`} />
                <Info label="Payout Frequency" value={dealOffer.payoutFrequency || "-"} />

                <Info label="Payout Threshold" value={dealOffer.payoutThreshold || "-"} />
                <Info label="Payout Currency" value={dealOffer.payoutCurrency || "-"} />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onClose={() => onCloseModal()} className="relative">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div>
          <div className={`flex justify-end`}>
            <X className="h-4 cursor-pointer mt-1" style={{ position: "absolute" }} onClick={() => onCloseModal()} />
          </div>
          <div
            className="bg-white elev-b-sm rounded-lg flex flex-col gap-4"
            style={{ overflowY: "auto", padding: 42, maxHeight: "98vh" }}
          >
            <div className="flex justify-between items-center">
              <KTitleSpan text={"Agreements and Payouts"} fontSize={24} lineHeight="26px" />
              <div className="flex flex-row gap-1 items-center">
                <KSpan text="(Selected User:" />
                <KSpan text={`${props.user?.firstName} ${props.user?.lastName})`} fontWeight={500} />
              </div>
            </div>

            <div>
              <div className="flex gap-8 mt-2">
                <div>
                  {renderUserExpense()}
                  {renderUserContract()}
                  {renderPaymentMethods()}
                </div>
                <div>
                  {renderUserTakedown()}
                  {renderDealDetails()}
                </div>
              </div>
              <div className="mt-4">{renderUserAdvance()}</div>
            </div>
          </div>
        </div>
        <AdminCurrencySelectionModal
          open={openCurrencySelection}
          userId={props.user?.id}
          onClose={() => setOpenCurrencySelection(false)}
          user={props?.user}
          selectedCurrencyId={selectedCurrencyId}
        />
      </div>
    </Dialog>
  )
}

export default AdminAgreementsPayoutModal
